import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { monitoringWebSocket } from '../../services/websocketService';
import type { PoolSiteSafetyAlertPayload } from '../pool/PoolSiteSafetyAlertDialog';

const DEFAULT_DURATION_SEC = 15;

/**
 * 통합/개별 관제 — control-center WS safety_alert 팝업.
 * 이벤트 「메시지 전송」은 시설 모니터만 대상으로 하며 관제에는 동일 팝업을 보내지 않음
 * (관제 닫기와 시설 경광 중지 혼동 방지). 다른 경로에서 관제로 오는 경우에만 표시.
 */
const ControlCenterSafetyAlertDialog: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState<PoolSiteSafetyAlertPayload | null>(null);
  const [remainingSec, setRemainingSec] = useState<number | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tickTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimers = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (tickTimerRef.current) {
      clearInterval(tickTimerRef.current);
      tickTimerRef.current = null;
    }
  }, []);

  const handleClose = useCallback(() => {
    clearTimers();
    setOpen(false);
    setPayload(null);
    setRemainingSec(null);
  }, [clearTimers]);

  const showAlert = useCallback(
    (msg: PoolSiteSafetyAlertPayload) => {
      const level = String(msg.level || '').toLowerCase();
      if (level && level !== 'danger' && level !== 'warning') {
        return;
      }
      const durRaw = Number(msg.duration_sec);
      const durationSec = Math.min(
        300,
        Math.max(
          3,
          Number.isFinite(durRaw) && durRaw > 0
            ? Math.floor(durRaw)
            : DEFAULT_DURATION_SEC
        )
      );

      clearTimers();
      setPayload(msg);
      setOpen(true);
      setRemainingSec(durationSec);

      closeTimerRef.current = setTimeout(() => {
        handleClose();
      }, durationSec * 1000);

      tickTimerRef.current = setInterval(() => {
        setRemainingSec((prev) => {
          if (prev == null || prev <= 1) return 0;
          return prev - 1;
        });
      }, 1000);
    },
    [clearTimers, handleClose]
  );

  useEffect(() => {
    return () => clearTimers();
  }, [clearTimers]);

  useEffect(() => {
    monitoringWebSocket.enableWebSocket();
    monitoringWebSocket.connectToControlCenter();

    const onSafetyAlert = (data: unknown) => {
      const raw = data as PoolSiteSafetyAlertPayload & {
        data?: PoolSiteSafetyAlertPayload;
      };
      if (!raw || typeof raw !== 'object') return;
      const nested = raw.data;
      const body: PoolSiteSafetyAlertPayload =
        nested && typeof nested === 'object'
          ? { ...nested, type: nested.type || raw.type }
          : raw;
      const bodyType = String(body.type || raw.type || '').toLowerCase();
      const level = String(body.level || '').toLowerCase();
      const isSafety =
        bodyType === 'safety_alert' ||
        level === 'danger' ||
        level === 'warning';
      if (!isSafety) return;
      if (!String(body.message || '').trim() && !level) return;
      showAlert(body);
    };

    monitoringWebSocket.subscribe('safety_alert', onSafetyAlert);
    return () => {
      monitoringWebSocket.unsubscribe('safety_alert', onSafetyAlert);
    };
  }, [showAlert]);

  if (!payload && !open) return null;

  const isDanger = String(payload?.level || 'danger').toLowerCase() === 'danger';
  const title = isDanger ? '위험 알림' : '경고 알림';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          position: 'relative',
          overflow: 'hidden',
          border: isDanger ? '3px solid #d32f2f' : '3px solid #ed6c02',
          bgcolor: isDanger ? '#1a0000' : '#1a1000',
          color: '#fff',
        },
      }}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 50 }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#fff' }}>
        <WarningAmberIcon color={isDanger ? 'error' : 'warning'} fontSize="large" />
        {title}
        {payload?.pool_name ? ` — ${payload.pool_name}` : null}
      </DialogTitle>
      <DialogContent>
        {(payload?.image_data_url || '').trim() ? (
          <Box
            component="img"
            src={(payload?.image_data_url || '').trim()}
            alt="이벤트 캡처"
            sx={{
              width: '100%',
              maxHeight: 320,
              objectFit: 'contain',
              borderRadius: 1,
              mb: 1.5,
              bgcolor: '#000',
            }}
          />
        ) : null}
        {[payload?.location, payload?.risk_type].filter(Boolean).length ? (
          <Typography variant="body2" sx={{ mb: 1, opacity: 0.85 }}>
            {[payload?.location, payload?.risk_type].filter(Boolean).join(' · ')}
          </Typography>
        ) : null}
        <Typography variant="h6" sx={{ mb: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {payload?.message || '관제에 위험 알림이 도착함. 즉시 확인'}
        </Typography>
        {remainingSec != null && remainingSec > 0 ? (
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            {remainingSec}초 후 자동으로 닫힘. (관제 미리보기 — 경광 중지는 시설 「닫기」)
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button variant="contained" color={isDanger ? 'error' : 'warning'} onClick={handleClose}>
          닫기
        </Button>
      </DialogActions>
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          animation: isDanger ? 'controlCenterAlertPulse 1.2s ease-in-out infinite' : 'none',
          '@keyframes controlCenterAlertPulse': {
            '0%, 100%': { boxShadow: 'inset 0 0 0 0 rgba(211,47,47,0)' },
            '50%': { boxShadow: 'inset 0 0 40px 8px rgba(211,47,47,0.35)' },
          },
        }}
      />
    </Dialog>
  );
};

export default ControlCenterSafetyAlertDialog;
